/**
 * Allowed tags for rich content (modals, onboarding, training screens).
 * Restrictive to prevent XSS while allowing common formatting.
 */
const DEFAULT_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "b",
  "i",
  "u",
  "ul",
  "ol",
  "li",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
  "code",
  "sub",
  "sup",
  "mark",
  "hr",
  "small",
  "img",
  "video",
  "source",
  "audio",
  "div",
  "span",
  "section",
  "header",
  "main",
  "figure",
  "figcaption",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
];

const DEFAULT_ALLOWED_ATTR = [
  "href",
  "src",
  "alt",
  "class",
  "title",
  "id",
  "rel",
  "width",
  "height",
  "poster",
  "type",
];

/**
 * Sanitize HTML before injecting via dangerouslySetInnerHTML.
 * On server (SSR) we escape angle brackets to prevent any tag execution.
 * On client we use DOMPurify with an allowlist (loaded only in browser to avoid SSR issues).
 */
export function sanitizeHtml(html: string): string {
  if (typeof window === "undefined") {
    // SSR: escape so no tags execute
    return html.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
  // CJS: module is the instance; ESM: module.default is the instance. require() used so DOMPurify loads only in browser (avoids SSR).
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const purifier = require("dompurify") as {
    default?: { sanitize: (d: string, c?: object) => string };
    sanitize?: (d: string, c?: object) => string;
  };
  const DOMPurify = purifier.default ?? purifier;
  const sanitize = DOMPurify.sanitize;
  if (!sanitize) return html.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return sanitize.call(DOMPurify, html, {
    ALLOWED_TAGS: DEFAULT_ALLOWED_TAGS,
    ALLOWED_ATTR: DEFAULT_ALLOWED_ATTR,
    ADD_ATTR: [
      "target",
      "controls",
      "autoplay",
      "loop",
      "playsinline",
      "muted",
      "style",
    ],
  });
}
